.toastRegion {
  position: fixed;
  z-index: var(--z-toast);
  inset-inline-end: var(--space-4);
  bottom: var(--space-4);
  display: flex;
  flex-direction: column-reverse;
  gap: var(--space-2);
  width: min(400px, calc(100vw - 2 * var(--space-4)));
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-2) var(--space-3) var(--space-4);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  background: var(--color-bg);
  color: var(--color-text-primary);
  box-shadow: var(--shadow-overlay);
  pointer-events: auto;
  animation: toast-in var(--duration-moderate-02) var(--ease-entrance);
}

/* Блок внимания (DESIGN.md 1.5): одна подложка цвета смысла без рамки, заголовок, строка объяснения, действия. */
.banner {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: 14px var(--space-3) 14px var(--space-4);
  border: 1px solid transparent;
  border-radius: 10px;
  color: var(--color-text-primary);
}

.bannerInline {
  border-radius: 0;
  border-inline: 0;
}

.tone-info {
  background: var(--color-info-bg);
}

.tone-success {
  background: var(--color-success-bg);
}

.tone-warning {
  background: var(--color-warning-bg);
}

.tone-error {
  background: var(--color-error-bg);
}

.banner.tone-error .title,
.banner.tone-error .text {
  color: var(--color-error-text);
}

.banner.tone-warning .title {
  color: var(--color-warning);
}

@media (forced-colors: active) {
  .banner {
    border-color: CanvasText;
  }
}

.toast.tone-info,
.toast.tone-success,
.toast.tone-warning,
.toast.tone-error {
  background: var(--color-bg);
  border-color: var(--color-border-subtle);
}

.statusIcon {
  flex-shrink: 0;
  margin-top: 2px;
}

.iconInfo {
  fill: var(--color-info-icon);
}

.iconSuccess {
  fill: var(--color-success-icon);
}

.iconWarning {
  fill: var(--color-warning-icon);
}

.iconWarning [data-icon-path='inner-path'] {
  fill: var(--color-warning-icon-inner);
  opacity: 1;
}

.iconError {
  fill: var(--color-error-icon);
}

.content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
  padding-top: 1px;
}

.title {
  font-size: var(--type-body-sm-size);
  line-height: var(--type-body-sm-line);
  font-weight: var(--weight-semibold);
}

.text {
  color: var(--color-text-primary);
  font-size: var(--type-body-sm-size);
  line-height: var(--type-body-sm-line);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-top: var(--space-2);
}

.close {
  flex-shrink: 0;
  margin-top: -6px;
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
}

.skeleton {
  display: block;
  border-radius: var(--radius-md);
  background: linear-gradient(90deg, var(--color-skeleton) 0%, var(--color-skeleton-shine) 50%, var(--color-skeleton) 100%);
  background-size: 200% 100%;
  animation: shimmer 1400ms linear infinite;
}

.skeletonText {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.skeletonLine {
  height: 12px;
  border-radius: var(--radius-sm);
}

@keyframes shimmer {
  from {
    background-position: 100% 0;
  }
  to {
    background-position: -100% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton {
    animation: none;
    background: var(--color-skeleton);
  }

  .toast {
    animation: none;
  }
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  min-height: 24px;
  padding: 2px 0;
  border-radius: var(--radius-pill);
  font-size: 0.8125rem;
  line-height: var(--type-caption-line);
  font-weight: var(--weight-medium);
  letter-spacing: 0;
  white-space: nowrap;
  vertical-align: middle;
}

.pill svg {
  flex-shrink: 0;
}

.pillLabel {
  overflow: hidden;
  text-overflow: ellipsis;
}

.pill.tone-neutral {
  color: var(--color-text-secondary);
}

.pill.tone-neutral svg {
  fill: var(--color-icon-secondary);
}

.pill.tone-info {
  color: var(--color-info);
}

.pill.tone-success {
  color: var(--color-success);
}

.pill.tone-warning {
  color: var(--color-warning);
}

.pill.tone-error {
  color: var(--color-error);
}

.pill.tone-info svg {
  fill: var(--color-info-icon);
}

.pill.tone-success svg {
  fill: var(--color-success-icon);
}

.pill.tone-warning svg {
  fill: var(--color-warning);
}

.pill.tone-error svg {
  fill: var(--color-error-icon);
}

/* Счётчик — число цветом смысла без плашки (DESIGN.md 1.5). */
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 20px;
  padding: 0 2px;
  border-radius: var(--radius-pill);
  background: transparent;
  color: var(--color-text-helper);
  font-size: var(--type-caption-size);
  line-height: 20px;
  font-weight: var(--weight-semibold);
  letter-spacing: 0;
  font-variant-numeric: tabular-nums;
}

.badgeAccent {
  color: var(--color-link);
}

.badgeDanger {
  color: var(--color-error);
  font-weight: var(--weight-bold);
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  max-width: 100%;
  min-height: 24px;
  padding: 0 var(--space-2);
  border: 1px solid transparent;
  border-radius: 7px;
  background: var(--color-layer-1);
  color: var(--color-text-primary);
  font-size: var(--type-body-sm-size);
  line-height: 22px;
  white-space: nowrap;
}

.tagMd {
  min-height: 32px;
  padding: 0 var(--space-3);
}

.tagRemove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-inline-end: -4px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--color-icon-secondary);
}

.tagRemove:hover {
  background: var(--color-layer-2);
}

.tagRemove svg {
  fill: currentColor;
}
